import { useCallback, useEffect, useMemo } from 'react';
import type {
	MasterDocumentSaveInvalidHandler,
	MasterDocumentSettingsForm,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/shared/useMasterDocumentSettingsForm.types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	DocumentWorkflowTemplateInfo,
	DocumentWorkflowTemplateType,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import { useUpdateMasterDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useUpdateMasterDocument.ts';
import {
	MasterDocumentFormDto,
	MasterDocumentFormShape,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/shared/masterDocumentForm.schema';
import { getSignatureType } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/getSignatureType.ts';
import { supportsWitnessField } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/supportsWitnessField';

const toFormDefaults = (template: DocumentWorkflowTemplateInfo): MasterDocumentFormDto => {
	const counterparties = (template.counterparties ?? []).map((cp) => ({
		name: cp.name,
		numberOfSignaturesRequired: cp.amount && cp.amount > 0 ? cp.amount : 1,
		requiresWitness: cp.requiresWitness ?? false,
	}));

	return {
		name: template.name,
		type: template.type,
		classifiers: template.classifiers ?? [],
		visibleIn: template.visibleIn ?? [],
		signatureType: template.signatureType ?? SignatureType.ELECTRONIC,
		counterparties,
		witnessRequired: template.witnessRequired ?? false,
		instruction: template.instruction ?? '',
		individualScheduleRequired: template.individualScheduleRequired ?? false,
		individualInstances: template.individualInstances ?? true,
		useManualSignaturePlacement: template.useManualSignaturePlacement ?? false,
		showStampMetadata: template.showStampMetadata ?? true,
		requiresPostDating: template.requiresPostDating ?? false,
		signaturePlacements: [],
		documentSource: template.globalDocumentTemplateId ? 'template' : 'upload',
		globalDocumentTemplateId: template.globalDocumentTemplateId ?? undefined,
	};
};

/** Maps validated form values onto the update-mutation payload. Pure so both form surfaces share one mapping. */
export const mapToUpdateParams = (templateId: string, values: MasterDocumentFormDto) => {
	const isSignatureRequired = values.type === DocumentWorkflowTemplateType.SIGNATURE_REQUIRED;
	const isSignableForm = values.type === DocumentWorkflowTemplateType.SIGNABLE_FORM;
	const hasSigningConfig = isSignatureRequired || isSignableForm;

	const signatureType = getSignatureType({
		type: values.type,
		formSignatureType: values.signatureType,
		omitForNonSigning: true,
	});

	return {
		templateId,
		name: values.name,
		classifiers: values.classifiers ?? [],
		visibleIn: values.visibleIn ?? [],
		signatureType,
		instruction: values.instruction ?? '',
		witnessRequired: isSignatureRequired
			? !!values.witnessRequired &&
				supportsWitnessField({
					signatureType: values.signatureType,
					individualInstances: values.individualInstances,
				})
			: undefined,
		individualScheduleRequired:
			values.individualInstances === false ? false : values.individualScheduleRequired,
		individualInstances: hasSigningConfig ? values.individualInstances : undefined,
		counterparties: isSignatureRequired
			? (values.counterparties ?? []).map((cp) => ({
					name: cp.name,
					numberOfSignaturesRequired: cp.numberOfSignaturesRequired,
					requiresWitness: cp.requiresWitness ?? false,
				}))
			: undefined,
		useManualSignaturePlacement: values.useManualSignaturePlacement,
		requiresPostDating: values.requiresPostDating,
		// signaturePlacements + showStampMetadata are owned by the placement
		// editor's Save button; undefined here means "leave existing value
		// alone", so the manual-placement toggle can flip without touching them.
		signaturePlacements: undefined,
		showStampMetadata: undefined,
		// datePlacements are owned by the placement editor, like signaturePlacements above.
		datePlacements: undefined,
	};
};

/**
 * Shared core of the Master Document settings form: schema-bound form state, defaults seeded
 * from the template, and a save path both the settings drawer and the inline editor drive.
 */
export const useMasterDocumentSettingsForm = (
	template: DocumentWorkflowTemplateInfo
): MasterDocumentSettingsForm => {
	const { mutateAsync, isPending } = useUpdateMasterDocument();

	const defaultValues = useMemo(() => toFormDefaults(template), [template]);

	const form = useForm<MasterDocumentFormDto>({
		mode: 'all',
		resolver: zodResolver(MasterDocumentFormShape),
		defaultValues,
		shouldFocusError: false,
	});

	useEffect(() => {
		form.reset(defaultValues);
	}, [defaultValues, form]);

	// Takes the invalid handler rather than leaving callers to wrap this in a second
	// handleSubmit: nesting them validated the form twice per click.
	const save = useCallback(
		async (onInvalid?: MasterDocumentSaveInvalidHandler) => {
			let written = false;
			await form.handleSubmit(async (values) => {
				await mutateAsync(mapToUpdateParams(template.id, values));
				written = true;
			}, onInvalid)();
			if (!written && !onInvalid) {
				throw new Error('Master document was not saved: the form is invalid');
			}
		},
		[form, mutateAsync, template.id]
	);

	return { form, save, isSaving: isPending };
};
